import React from "react";
import { format } from "date-fns";
import { Visit } from "../../../interfaces/Visit";

interface IPDEntry {
  id?: string | number;
  type: string;
  date: string;
  data: any;
  userId?: number;
  title?: string;
}

interface IPDEntryCardProps {
  entry: IPDEntry;
  visit: Visit | undefined;
  onEdit: () => void;
  onRefresh: () => void;
  onCommentsClick?: (entry: IPDEntry) => void;
}

const IPDEntryCard: React.FC<IPDEntryCardProps> = ({
  entry,
  visit,
  onEdit,
  onRefresh,
  onCommentsClick,
}) => {
  // onRefresh is used in parent; entry deletion is handled elsewhere.

  const isEmptyValue = (value: any) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") return value.trim() === "";
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "object") return Object.keys(value).length === 0;
    return false;
  };

  const formatLabel = (key: string) =>
    key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim();

  const formatPrimitive = (key: string, value: any) => {
    if (typeof value === "string") {
      const looksLikeDate =
        key.toLowerCase().includes("date") ||
        key.toLowerCase().includes("time");
      const parsed = new Date(value);
      if (looksLikeDate && !isNaN(parsed.getTime())) {
        return format(parsed, "dd MMM yyyy, HH:mm");
      }
    }
    return String(value);
  };

  const isLargeValue = (value: any) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 120;
    if (Array.isArray(value)) return value.length > 4;
    if (typeof value === "object") return Object.keys(value).length > 4;
    return false;
  };

  const isMediumValue = (value: any) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 60;
    if (Array.isArray(value)) return value.length > 2;
    if (typeof value === "object") return Object.keys(value).length > 2;
    return false;
  };

  const renderLabRequestDetails = () => {
    const data = entry?.data || {};
    const labRequest = data.lab_request || data.labRequest || null;

    if (!labRequest) {
      return (
        <div className="text-sm text-gray-500">
          No lab request details available.
        </div>
      );
    }

    const labTest = labRequest.lab_test || labRequest.labTest || {};
    const testName = labTest.name || "-";
    const testCode = labTest.code ? `(${labTest.code})` : "";
    const orderNumber = labRequest.order_number || labRequest.orderNumber || "-";
    const sampleId = labRequest.sample_id || labRequest.sampleId || "-";
    const status = labRequest.status || "-";
    const priority = labRequest.priority || "-";
    const charge = labRequest.charge != null ? labRequest.charge : "-";
    const referredBy = labRequest.referred_by || labRequest.referredBy || "-";
    const notes =
      data.notes || labRequest.notes || "";

    return (
      <div className="space-y-3">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Order Number
            </div>
            <div className="mt-1 text-sm text-gray-800">{orderNumber}</div>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Sample ID
            </div>
            <div className="mt-1 text-sm text-gray-800">{sampleId}</div>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Status
            </div>
            <div className="mt-1 text-sm text-gray-800 capitalize">{status}</div>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Priority
            </div>
            <div className="mt-1 text-sm text-gray-800 capitalize">{priority}</div>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Charge
            </div>
            <div className="mt-1 text-sm text-gray-800">{charge}</div>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Referred By
            </div>
            <div className="mt-1 text-sm text-gray-800">{referredBy}</div>
          </div>
        </div>
        <div className="pt-2 border-t border-gray-100">
          <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
            Lab Test
          </div>
          <div className="mt-1 text-sm font-medium text-gray-800">
            {testName} {testCode}
          </div>
        </div>
        {notes && (
          <div className="pt-2 border-t border-gray-100">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Notes
            </div>
            <div className="mt-1 text-sm text-gray-800">{notes}</div>
          </div>
        )}
      </div>
    );
  };

  const renderPharmacyRequestDetails = () => {
    const data = entry?.data || {};
    const pharmacyRequest =
      data.pharmacy_request || data.pharmacyRequest || null;

    if (!pharmacyRequest) {
      return (
        <div className="text-sm text-gray-500">
          No pharmacy request details available.
        </div>
      );
    }

    const saleNumber =
      pharmacyRequest.sale_number ||
      pharmacyRequest.saleNumber ||
      data.pharmacy_sale_id;
    const doctorName =
      pharmacyRequest.doctor?.name ||
      data.doctor?.name ||
      data.doctor_name ||
      "";
    const status = pharmacyRequest.status || data.status;
    const items = pharmacyRequest.items || [];

    return (
      <div className="space-y-3">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Sale Number
            </div>
            <div className="mt-1 text-sm text-gray-800">
              {saleNumber || "-"}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Sale Date
            </div>
            <div className="mt-1 text-sm text-gray-800">
              {entry.date
                ? format(new Date(entry.date), "dd MMM yyyy, HH:mm")
                : "-"}
            </div>
          </div>
          
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {items && items.length > 0 && (
            <div>
              <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Items
              </div>
              <ul className="mt-1 text-sm text-gray-800 list-disc list-inside">
                {items.map((item: any) => {
                  const pharmacyItem =
                    item.pharmacy_item || item.pharmacyItem || {};
                  const name = pharmacyItem.name || "-";
                  const sku = pharmacyItem.sku;
                  const quantity = item.quantity;
                  return (
                    <li key={item.id}>
                      {quantity} x {name}
                      {sku ? ` (${sku})` : ""}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {data.notes && (
            <div className="pt-2 border-t border-gray-100">
              <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Notes
              </div>
              <div className="mt-1 text-sm text-gray-800">{data.notes}</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getColumnClass = (itemCount: number) => {
    if (itemCount <= 3) return "grid-cols-3";
    if (itemCount <= 6) return "grid-cols-2";
    return "grid-cols-1";
  };

  const renderValue = (key: string, value: any) => {
    if (Array.isArray(value)) {
      return (
        <ul className="text-sm list-disc list-inside text-gray-700">
          {value.map((item, idx) => (
            <li key={idx}>{renderValue(key, item)}</li>
          ))}
        </ul>
      );
    }

    if (typeof value === "object" && value !== null) {
      const label =
        value.label || value.name || value.title || value.value || value.id;
      if (
        label !== undefined &&
        label !== null &&
        String(label).trim() !== ""
      ) {
        return <span className="text-sm text-gray-800">{String(label)}</span>;
      }

      const entries = Object.entries(value).filter(([, v]) => !isEmptyValue(v));

      if (entries.length === 0) return null;

      const columnClass = getColumnClass(entries.length);

      return (
        <div className={`grid gap-3 ${columnClass}`}>
          {entries.map(([childKey, childValue]) => (
            <div key={childKey} className="min-w-0 text-sm text-gray-700">
              <span className="text-gray-500">{formatLabel(childKey)}: </span>
              {renderValue(childKey, childValue)}
            </div>
          ))}
        </div>
      );
    }

    return (
      <span className="text-sm text-gray-800">
        {formatPrimitive(key, value)}
      </span>
    );
  };

  const renderEntryContent = () => {
    const data = entry?.data || {};
    // Exclude 'comments' from main grid when we show it as a clickable row for doctor_rounds
    // Custom rendering for specific entry types
    if (entry.type === "pharmacy_request") {
      return renderPharmacyRequestDetails();
    }
    if (entry.type === "lab_request") {
      return renderLabRequestDetails();
    }

    const keys = Object.keys(data).filter(
      (key) => key !== "comments" && !isEmptyValue(data[key]),
    );

    if (keys.length === 0 && entry.type !== "doctor_rounds") {
      return <div className="text-sm text-gray-500">No details available.</div>;
    }

    const columnClass = getColumnClass(keys.length);

    return (
      <>
        {keys.length > 0 && (
          <div className={`grid gap-4 ${columnClass}`}>
            {keys.map((key) => (
              <div key={key} className="min-w-0">
                <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                  {formatLabel(key)}
                </div>
                <div className="mt-1">{renderValue(key, data[key])}</div>
              </div>
            ))}
          </div>
        )}
        {/* Comments row for doctor rounds - click opens modal with rich text editor */}
        {entry.type === "doctor_rounds" && onCommentsClick && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={() => onCommentsClick(entry)}
              className="flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-900"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                />
              </svg>
              {data.comments ? "View / Edit comments" : "Add comments"}
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="pt-2 lg:pt-5">
      <div className="absolute w-3 h-3 bg-primary rounded-full mt-1.5 -left-1.5 border border-white dark:border-gray-900 dark:bg-gray-700"></div>
      <time className="mb-1 text-sm font-normal leading-none text-active dark:text-gray-500">
        {format(new Date(entry.date), "dd MMM yyyy, HH:mm")}
      </time>
      <div className="overflow-hidden bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 border-t border-gray-200 sm:px-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-medium text-gray-900">{entry.title}</h3>
            <div className="flex gap-2">
              <button
                onClick={onEdit}
                className="px-3 py-1 text-sm font-medium text-indigo-600 hover:text-indigo-900"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
            </div>
          </div>
          {renderEntryContent()}
        </div>
      </div>
    </div>
  );
};

export default IPDEntryCard;
